/**
 * Consolidated form state management hook
 */

import { useState, useCallback } from 'react';
import {
    serviceNameValidator,
    requiredValidator,
    minLengthValidator,
    maintainerTeamsValidator
} from '../utils/validators';
import { useBaseInput } from '../components/BaseInput';
import { useMaintainerTeamsInput } from '../components/MaintainerTeamsInput';
import { func } from 'prop-types';

export const useFormState = () => {
    // Basic form fields using useBaseInput
    const serviceName = useBaseInput({
        required: true,
        minLength: 3,
        customValidator: serviceNameValidator,
        requiredMessage: 'Service name is required'
    });

    const description = useBaseInput({
        required: true,
        minLength: 5,
        maxLength: 500,
        requiredMessage: 'Description is required',
    });

    // Select fields state
    const [techStack, setTechStack] = useState<string>('');
    const [techStackError, setTechStackError] = useState<string>('');

    const [orgName, setOrgName] = useState<string>('');
    const [orgNameError, setOrgNameError] = useState<string>('');

    const [domain, setDomain] = useState<string>('');
    const [domainError, setDomainError] = useState<string>('');

    const [functionalArea, setFunctionalArea] = useState<string>('');
    const [functionalAreaError, setFunctionalAreaError] = useState<string>('');

    // Select field validators
    const validateTechStack = useCallback(() => {
        const error = requiredValidator(techStack, 'Technology stack');
        setTechStackError(error);
        return error;
    }, [techStack]);

    const validateOrgName = useCallback(() => {
        const error = requiredValidator(orgName, 'Organization');
        setOrgNameError(error);
        return error;
    }, [orgName]);

    const validateDomain = useCallback(() => {
        const error = requiredValidator(domain, 'Domain');
        setDomainError(error);
        return error;
    }, [domain]);

    const validateFunctionalArea = useCallback(() => {
        const error = requiredValidator(functionalArea, 'Functional Area');
        setFunctionalAreaError(error);
        return error;
    }, [functionalArea]);

    // Select field handlers (PatternFly signature)
    const handleTechStackChange = useCallback((
        _event: React.MouseEvent<Element, MouseEvent> | undefined,
        value: string | number | undefined
    ) => {
        const stringValue = typeof value === 'string' ? value : (value?.toString() || '');
        setTechStack(stringValue);
        if (techStackError && stringValue) {
            setTechStackError('');
        }
    }, [techStackError]);

    const handleOrgNameChange = useCallback((
        _event: React.MouseEvent<Element, MouseEvent> | undefined,
        value: string | number | undefined
    ) => {
        const stringValue = typeof value === 'string' ? value : (value?.toString() || '');
        setOrgName(stringValue);
        if (orgNameError && stringValue) {
            setOrgNameError('');
        }
    }, [orgNameError]);

    const handleDomainChange = useCallback((
        _event: React.MouseEvent<Element, MouseEvent> | undefined,
        value: string | number | undefined
    ) => {
        const stringValue = typeof value === 'string' ? value : (value?.toString() || '');
        setDomain(stringValue);
        if (domainError && stringValue) {
            setDomainError('');
        }
    }, [domainError]);

    const handleFunctionalAreaChange = useCallback((
        _event: React.MouseEvent<Element, MouseEvent> | undefined,
        value: string | number | undefined
    ) => {
        const stringValue = typeof value === 'string' ? value : (value?.toString() || '');
        setFunctionalArea(stringValue);
        if (functionalAreaError && stringValue) {
            setFunctionalAreaError('');
        }
    }, [functionalAreaError]);

    // Reset form function
    const resetForm = useCallback(() => {
        // Reset input fields
        serviceName.setValue('');
        description.setValue('');

        // Reset select fields
        setTechStack('');
        setOrgName('');
        setDomain('');

        // Clear all errors
        serviceName.setError('');
        description.setError('');
        setTechStackError('');
        setOrgNameError('');
        setDomainError('');
    }, [serviceName, description]);

    // Validation state
    const isTechStackValid = !techStackError && !!techStack;
    const isOrgNameValid = !orgNameError && !!orgName;
    const isDomainValid = !domainError && !!domain;
    const isFunctionalAreaValid = !functionalAreaError && !!functionalArea;

    return {
        // Input fields
        serviceName: {
            value: serviceName.value,
            error: serviceName.error,
            handleChange: serviceName.handleChange,
            handleBlur: serviceName.handleBlur,
            validate: serviceName.validate,
            setError: serviceName.setError,
            isValid: serviceName.isValid
        },
        description: {
            value: description.value,
            error: description.error,
            handleChange: description.handleChange,
            handleBlur: description.handleBlur,
            validate: description.validate,
            setError: description.setError,
            isValid: description.isValid
        },

        // Select fields
        techStack: {
            value: techStack,
            error: techStackError,
            handleChange: handleTechStackChange,
            validate: validateTechStack,
            setError: setTechStackError,
            setValue: setTechStack,
            isValid: isTechStackValid
        },
        orgName: {
            value: orgName,
            error: orgNameError,
            handleChange: handleOrgNameChange,
            validate: validateOrgName,
            setError: setOrgNameError,
            setValue: setOrgName,
            isValid: isOrgNameValid
        },
        domain: {
            value: domain,
            error: domainError,
            handleChange: handleDomainChange,
            validate: validateDomain,
            setError: setDomainError,
            setValue: setDomain,
            isValid: isDomainValid
        },
        functionalArea: {
            value: functionalArea,
            error: functionalAreaError,
            handleChange: handleFunctionalAreaChange,
            validate: validateFunctionalArea,
            setError: setFunctionalAreaError,
            setValue: setFunctionalArea,
            isValid: isFunctionalAreaValid
        },

        // Utility functions
        resetForm
    };
};
